// База API: REACT_APP_BASE_URL задаётся в Docker/CI (dev/prod). Локально `npm start` без .env → localhost.
const resolveBaseUrl = () => {
  const fromEnv = process.env.REACT_APP_BASE_URL;
  if (fromEnv != null && String(fromEnv).trim() !== '') {
    return String(fromEnv).replace(/\/$/, '');
  }
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8080';
  }
  return 'https://app-dev.tesalut.md:443';
};

const BASE_URL = resolveBaseUrl();
const API_BASE = `${BASE_URL}/api`;

const ALLOWED_ROLES = {
  SUPPORT: 'SUPPORT',
  MODERATOR: 'MODERATOR',
  ADMIN: 'ADMIN',
  // DEV — высшая роль, стоит над ADMIN. Обладает всеми правами админа
  // плюс эксклюзивные возможности (баннеры, создание сотрудников-DEV/ADMIN).
  DEV: 'DEV'
};

// Переменная для отслеживания процесса обновления токена
// Это предотвращает множественные одновременные запросы на обновление
let refreshTokenPromise = null;
let accessRolePromise = null;
let cachedAccessRole = null;
const reviewsRequestInFlight = new Map();
const reviewsResponseCache = new Map();
const REVIEWS_CACHE_TTL_MS = 5000;
const userDetailsRequestInFlight = new Map();
const userDetailsResponseCache = new Map();
const USER_DETAILS_CACHE_TTL_MS = 5000;

// Функция для обновления токена
const refreshToken = async () => {
  // Если уже идет процесс обновления, ждем его завершения
  if (refreshTokenPromise) {
    return await refreshTokenPromise;
  }

  // Создаем новый промис для обновления токена
  refreshTokenPromise = (async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (!refreshTokenValue) {
        throw new Error('No refresh token');
      }

      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          // бэкенд ожидает refresh-token в заголовке, а не в теле
          'refresh-token': refreshTokenValue,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        // чистим, чтобы не ходить бесконечно с плохим токеном
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      localStorage.setItem('accessToken', data.accessToken);
      // Сохраняем новый refresh token, если он возвращается
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      return data.accessToken;
    } catch (error) {
      // При любой ошибке очищаем токены
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw error;
    } finally {
      // Очищаем промис после завершения (успешного или нет)
      refreshTokenPromise = null;
    }
  })();

  return await refreshTokenPromise;
};

// Токен считаем непригодным чуть раньше реального exp: пока фрейм CONNECT
// долетит до бэкенда, «почти истёкший» токен уже может стать невалидным.
const TOKEN_EXPIRY_SKEW_MS = 60 * 1000;

const isAccessTokenUsable = (jwt) => {
  if (!jwt) return false;
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    return payload?.exp * 1000 - TOKEN_EXPIRY_SKEW_MS > Date.now();
  } catch {
    return false;
  }
};

/**
 * Актуальный access token для тех мест, где заголовок ставится вручную и
 * авто-обновления из authenticatedFetch нет (STOMP-фрейм CONNECT, XHR-загрузки).
 * Истёкший токен обновляется молча; при провале refresh возвращает null.
 *
 * @param {{ force?: boolean }} [options] force — обновить, даже если срок ещё не вышел
 *   (бэкенд отверг текущий токен: он мог быть отозван раньше exp).
 */
const getFreshAccessToken = async ({ force = false } = {}) => {
  const token = localStorage.getItem('accessToken');
  if (!force && isAccessTokenUsable(token)) return token;
  if (!localStorage.getItem('refreshToken')) return force ? null : token || null;
  try {
    return await refreshToken();
  } catch {
    return null;
  }
};

// Универсальная функция для выполнения аутентифицированных запросов
// Автоматически обрабатывает 401/403 ошибки и обновляет токен
const authenticatedFetch = async (url, options = {}) => {
  // Получаем текущий токен
  let token = localStorage.getItem('accessToken');
  let skipRefreshRetry = false;

  const isTokenExpired = (jwt) => {
    if (!jwt) return true;
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      return !(payload?.exp * 1000 > Date.now());
    } catch {
      return true;
    }
  };

  // Пробуем обновить access token заранее, чтобы не делать "первый 401 + повторный запрос"
  const hasRefreshToken = Boolean(localStorage.getItem('refreshToken'));
  if ((isTokenExpired(token) || !token) && hasRefreshToken) {
    try {
      token = await refreshToken();
    } catch {
      // refresh уже очистил токены; не пытаемся второй раз обновлять внутри 401-ветки
      skipRefreshRetry = true;
    }
  }

  // Функция для выполнения запроса с токеном
  const makeRequest = async (tokenToUse, isRetry = false) => {
    // Клонируем options, чтобы не модифицировать оригинал
    const requestOptions = { ...options };

    // FormData сам выставляет multipart/form-data с нужным boundary —
    // если задать Content-Type вручную, браузер не сможет его выставить.
    const isFormData = typeof FormData !== 'undefined' && requestOptions.body instanceof FormData;

    // Клонируем headers
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...requestOptions.headers,
    };

    // Добавляем токен авторизации, если он есть
    if (tokenToUse) {
      headers['Authorization'] = `Bearer ${tokenToUse}`;
    }

    // Если есть body, убеждаемся, что это строка (для повторных запросов), кроме FormData
    if (requestOptions.body && !isFormData && typeof requestOptions.body !== 'string') {
      requestOptions.body = JSON.stringify(requestOptions.body);
    }

    const response = await fetch(url, {
      ...requestOptions,
      headers,
    });

    // Если получили 401/403, пытаемся обновить токен (только один раз)
    const isAuthErrorStatus = response.status === 401 || response.status === 403;
    if (isAuthErrorStatus && !isRetry && !skipRefreshRetry) {
      try {
        // Обновляем токен
        const newToken = await refreshToken();
        // Повторяем запрос с новым токеном (помечаем как повторную попытку)
        return await makeRequest(newToken, true);
      } catch (refreshError) {
        // При падении refresh не маскируем проблему - дальше приложение обработает выход в login
        throw new Error('Authentication failed. Please login again.');
      }
    }

    return response;
  };

  return await makeRequest(token, false);
};

// Функция проверки доступа
const checkAccess = async () => {
  if (cachedAccessRole) {
    return cachedAccessRole;
  }

  if (accessRolePromise) {
    return accessRolePromise;
  }

  accessRolePromise = (async () => {
  const response = await authenticatedFetch(`${API_BASE}/support/access`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Access check failed: ${response.status} ${response.statusText}`);
  }

  // Получаем сырой текст ответа
  const responseText = await response.text();

  let role;

  // Пробуем парсить как JSON, если это валидный JSON
  try {
    const jsonData = JSON.parse(responseText);

    // Если это строка - используем как есть
    if (typeof jsonData === 'string') {
      role = jsonData;
    }
    // Если это объект - ищем поле с ролью
    else if (typeof jsonData === 'object') {
      role = jsonData.role || jsonData.userRole || jsonData.authority || jsonData.response;
    }
  } catch (e) {
    // Если не JSON - используем как текст
    console.log('Response is not JSON, using as text');
    role = responseText;
  }

  // Нормализуем роль
  role = role?.toString().trim().toUpperCase();

  if (!role || !Object.values(ALLOWED_ROLES).includes(role)) {
    console.error('Invalid role received. Role:', role, 'Allowed:', Object.values(ALLOWED_ROLES));
    throw new Error(`Invalid role: ${role}`);
  }

    cachedAccessRole = role;
    return role;
  })();

  try {
    return await accessRolePromise;
  } finally {
    accessRolePromise = null;
  }
};

// Функция логина
const login = async (phoneNumber, password, role) => {
  const loginUrl = `${API_BASE}/auth/login`;

  // Создаем объект с данными для отправки - БЕЗ поля gender
  const requestBody = {
    phoneNumber,
    password,
    role,
    userInterface: 'WEB'
  };

  console.log('🔐 Attempting login to:', loginUrl);
  console.log('📋 Request body:', JSON.stringify(requestBody, null, 2));

  const response = await fetch(loginUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  console.log('📡 Response status:', response.status, response.statusText);

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
      console.error('❌ Login failed:', errorData);
      // Выводим понятное сообщение об ошибке
      throw new Error(errorData.message || 'Login failed');
    } catch (e) {
      if (e.message !== 'Login failed') {
        console.error('❌ Login failed - could not parse error');
        throw new Error('Login failed');
      }
      throw e;
    }
  }

  const data = await response.json();
  console.log('✅ Login successful');
  return data;
};

// Получение списка пользователей (UserSearchRequest.categoryId)
const getUsers = async (
  page = 0,
  size = 10,
  sortBy = 'fullName',
  name = '',
  phone = '',
  role = '',
  status = '',
  direction = 'ASC',
  createdFrom = '',
  createdTo = '',
  id = '',
  ratingFrom = '',
  ratingTo = '',
  categoryId = null,
  verified = '',
  regionCodes = [],
  ordersCount = '',
  ordersCountOrMore = false,
  marketingConsent = ''
) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('size', size);
  const backendSort = sortBy === 'id' ? 'fullName' : sortBy;
  params.append('sortBy', backendSort);
  if (direction) params.append('direction', direction);
  if (name) params.append('name', name);
  if (phone) params.append('phone', phone);
  if (role) params.append('role', role);
  if (status) params.append('status', status);
  if (Array.isArray(regionCodes)) {
    regionCodes.forEach((code) => params.append('regionCodes', code));
  }
  if (verified === true || verified === false) {
    params.append('verified', String(verified));
  } else if (verified === 'true' || verified === 'false') {
    params.append('verified', verified);
  }
  if (marketingConsent === true || marketingConsent === false) {
    params.append('marketingConsent', String(marketingConsent));
  } else if (marketingConsent === 'true' || marketingConsent === 'false') {
    params.append('marketingConsent', marketingConsent);
  }
  if (id) {
    params.append('id', id);
    params.append('userId', id); // Compatibility
  }

  if (ratingFrom !== '' && ratingFrom != null) {
    const n = Number(ratingFrom);
    if (!Number.isNaN(n)) params.append('ratingFrom', String(Math.trunc(n)));
  }
  if (ratingTo !== '' && ratingTo != null) {
    const n = Number(ratingTo);
    if (!Number.isNaN(n)) params.append('ratingTo', String(Math.trunc(n)));
  }

  if (categoryId != null && String(categoryId).trim() !== '') {
    params.append('categoryId', String(categoryId).trim());
  }

  if (ordersCount !== '' && ordersCount != null) {
    const n = Number(ordersCount);
    if (!Number.isNaN(n)) {
      params.append('ordersCount', String(Math.trunc(n)));
      params.append('ordersCountOrMore', String(Boolean(ordersCountOrMore)));
    }
  }

  if (createdFrom) {
    const date = new Date(createdFrom);
    params.append('createdFrom', date.toISOString()); // формат для ZonedDateTime
  }

  if (createdTo) {
    const date = new Date(createdTo);
    params.append('createdTo', date.toISOString()); // формат для ZonedDateTime
  }

  const response = await authenticatedFetch(
    `${API_BASE}/support/users?${params.toString()}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }

  return response.json();
};

// Получение деталей пользователя
const getUserDetails = async (userId) => {
  const requestKey = String(userId);
  const now = Date.now();
  const cachedEntry = userDetailsResponseCache.get(requestKey);

  if (cachedEntry && now - cachedEntry.timestamp < USER_DETAILS_CACHE_TTL_MS) {
    return cachedEntry.data;
  }

  if (userDetailsRequestInFlight.has(requestKey)) {
    return userDetailsRequestInFlight.get(requestKey);
  }

  const requestPromise = (async () => {
    const response = await authenticatedFetch(`${API_BASE}/support/users/${userId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user details');
    }

    const data = await response.json();
    userDetailsResponseCache.set(requestKey, { data, timestamp: Date.now() });
    return data;
  })();

  userDetailsRequestInFlight.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    userDetailsRequestInFlight.delete(requestKey);
  }
};
const searchOrders = async ({
  title = '',
  status = '',
  language = '',
  createdFrom = '',
  createdTo = '',
  id = '',
  userId = '',
  neverBought = null,
  inWorkFrom = '',
  inWorkTo = '',
  doneFrom = '',
  doneTo = '',
  canceledFrom = '',
  canceledTo = '',
  page = 0,
  size = 10,
  sort = 'createdAt',
  direction = 'DESC'
} = {}) => {
  const params = new URLSearchParams();

  if (title) params.append('title', title);
  if (status) params.append('status', status);
  if (language) params.append('language', language);

  if (createdFrom) {
    const date = new Date(createdFrom);
    params.append('createdFrom', date.toISOString()); // ✅ формат для ZonedDateTime
  }

  if (createdTo) {
    const date = new Date(createdTo);
    params.append('createdTo', date.toISOString()); // ✅
  }

  // Даты перехода в статусы IN_WORK / DONE / CANCELED (ZonedDateTime на бэкенде)
  const statusDates = { inWorkFrom, inWorkTo, doneFrom, doneTo, canceledFrom, canceledTo };
  Object.entries(statusDates).forEach(([key, value]) => {
    if (value) params.append(key, new Date(value).toISOString());
  });

  // Если передан id в поиске, используем его как userId для получения всех заказов пользователя
  if (id) {
    params.append('userId', id);
  } else if (userId) {
    params.append('userId', userId);
  }

  if (neverBought !== null && neverBought !== undefined) {
    params.append('neverBought', neverBought.toString());
  }

  params.append('page', page);
  params.append('size', size);

  // Добавляем параметры сортировки для pageable
  // По умолчанию для поиска по пользователю используем updatedAt, как в примере пользователя
  const backendSort = sort === 'id' ? 'updatedAt' : sort;
  if (backendSort) params.append('sort', backendSort);
  if (direction) params.append('direction', direction);

  const response = await authenticatedFetch(`${API_BASE}/support/orders?${params.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch orders: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Получение деталей заказа
const getOrderDetails = async (orderId) => {
  const response = await authenticatedFetch(`${API_BASE}/support/orders/${orderId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch order details: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Получение списка компаний для заказа
const getOrderCompanies = async (orderId) => {
  const response = await authenticatedFetch(`${API_BASE}/support/orders/${orderId}/companies`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch order companies: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Обновление статуса заказа модератором
const updateOrderStatus = async (orderId, status, masterUserProfileId) => {
  const response = await authenticatedFetch(`${API_BASE}/moder/orders/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, masterUserProfileId: masterUserProfileId || null }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update order status: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Внутренние заметки саппорта по заказу
const getOrderComments = async (orderId) => {
  const response = await authenticatedFetch(`${API_BASE}/support/orders/${orderId}/comments`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch order comments: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

const addOrderComment = async (orderId, text) => {
  const params = new URLSearchParams();
  params.append('text', text);

  const response = await authenticatedFetch(
    `${API_BASE}/support/orders/${orderId}/comment?${params.toString()}`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to add order comment: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

const editOrderComment = async (commentId, text) => {
  const params = new URLSearchParams();
  params.append('text', text);

  const response = await authenticatedFetch(
    `${API_BASE}/support/orders/comment/${commentId}?${params.toString()}`,
    {
      method: 'PUT',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to edit order comment: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

const deleteOrderComment = async (commentId) => {
  const response = await authenticatedFetch(`${API_BASE}/support/orders/comment/${commentId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete order comment: ${response.status} ${response.statusText}`);
  }

  return true;
};

// Обновление пользователя через новый эндпоинт
// Принимает userId, текущие данные пользователя (currentUser) и объект с изменяемыми полями (updateData)
const updateUserStatus = async (userId, updateData, currentUser = null) => {
  // Если currentUser не передан, получаем его
  let userData = currentUser;
  if (!userData) {
    userData = await getUserDetails(userId);
  }

  // Если updateData - строка (старый формат), используем только статус
  const isLegacyFormat = typeof updateData === 'string';
  const newStatus = isLegacyFormat ? updateData : (updateData?.status || userData.status);

  // Формируем полный объект UserProfileDto на основе текущих данных
  // Обновляем только те поля, которые были переданы в updateData
  const userProfileDto = {
    id: userData.id,
    userLoginId: userData.userLoginId || userData.id, // Используем id если userLoginId нет
    avatar: userData.avatar || null,
    fullName: (updateData?.fullName && !isLegacyFormat) ? updateData.fullName.trim() : userData.fullName,
    phoneNumbers: userData.phoneNumbers || [],
    defaultPhoneNumber: userData.defaultPhoneNumber || null,
    dateOfBirth: userData.dateOfBirth || null,
    email: (updateData?.email && !isLegacyFormat) ? (updateData.email.trim() || null) : (userData.email || null),
    gender: (updateData?.gender && !isLegacyFormat) ? updateData.gender : (userData.gender || null),
    preferredLanguage: (updateData?.preferredLanguage && !isLegacyFormat) ? updateData.preferredLanguage : (userData.preferredLanguage || null),
    createDate: userData.createDate,
    updateDate: userData.updateDate,
    plan: (updateData?.plan && !isLegacyFormat) ? updateData.plan : userData.plan,
    subscriptionExpireDate: userData.subscriptionExpireDate || null,
    socialMediaLinks: userData.socialMediaLinks || null,
    status: newStatus,
    address: userData.address || null,
    rating: userData.rating || null,
    countRating: userData.countRating || null,
    referralCode: (updateData?.referralCode && !isLegacyFormat) ? (updateData.referralCode.trim() || null) : (userData.referralCode || null),
    // referralUsersCount в БД имеет NOT NULL, поэтому не отправляем null.
    // Если в данных 0, оставляем 0, иначе подставляем 0 по умолчанию.
    referralUsersCount: userData.referralUsersCount ?? 0,
    referralUsersCountPeriod: userData.referralUsersCountPeriod || null,
    referralBonus: userData.referralBonus || null,
    role: userData.role,
    companyName: userData.companyName || null,
    balance: userData.balance || null,
    companyRating: userData.companyRating || null,
    verified: Boolean(userData.verified),
    description: userData.description || null
  };

  const response = await authenticatedFetch(`${API_BASE}/moder/users/users`, {
    method: 'PUT',
    body: JSON.stringify(userProfileDto)
  });

  if (!response.ok) {
    let errorMessage = `Failed to update user: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData?.message || errorData?.error || errorMessage;
    } catch {
      // Игнорируем ошибки парсинга
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

const COMPLAINTS_REQUEST_TTL_MS = 15000;
const complaintsInFlight = new Map();
const complaintsResponseCache = new Map();

const getComplaints = async (
  page = 0,
  size = 10,
  status = [],
  targetType = '',
  search = '',
  sort = 'createdAt',
  direction = 'DESC',
  fetchOptions = {}
) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('size', size);
  if (sort) params.append('sort', sort);
  if (direction) params.append('direction', direction);
  if (status && Array.isArray(status) && status.length > 0) {
    status.forEach(s => params.append('status', s));
  }
  if (targetType) params.append('targetType', targetType);
  if (search && search.trim()) params.append('search', search.trim());

  const key = params.toString();
  const now = Date.now();
  const cached = complaintsResponseCache.get(key);
  if (cached && now - cached.timestamp < COMPLAINTS_REQUEST_TTL_MS) {
    return cached.data;
  }

  const inFlight = complaintsInFlight.get(key);
  if (inFlight) {
    return inFlight;
  }

  const requestPromise = (async () => {
    const response = await authenticatedFetch(
      `${API_BASE}/support/complaints?${params.toString()}`,
      {
        method: 'GET',
        ...fetchOptions,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch complaints');
    }

    const data = await response.json();
    complaintsResponseCache.set(key, { data, timestamp: Date.now() });
    if (complaintsResponseCache.size > 50) {
      const oldestKey = complaintsResponseCache.keys().next().value;
      complaintsResponseCache.delete(oldestKey);
    }
    return data;
  })();

  complaintsInFlight.set(key, requestPromise);
  try {
    return await requestPromise;
  } finally {
    complaintsInFlight.delete(key);
  }
};

// Проверка валидности токена.
// Если access token истёк, пробуем silent refresh и считаем сессию валидной
// только когда refresh завершился успешно.
const isTokenValid = async () => {
  const token = localStorage.getItem('accessToken');
  const refreshTokenValue = localStorage.getItem('refreshToken');

  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload?.exp * 1000 > Date.now()) {
        return true;
      }
    } catch {
      // ignore parse errors and fallback to refresh flow
    }
  }

  if (!refreshTokenValue) {
    return false;
  }

  try {
    await refreshToken();
    return true;
  } catch {
    return false;
  }
};
const getComplaintById = async (complaintId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/support/complaints/${complaintId}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch complaint details');
  }

  return response.json();
};

// Обновление статуса жалобы
const updateComplaintStatus = async (complaintId, status) => {
  const response = await authenticatedFetch(
    `${API_BASE}/support/complaints/${complaintId}/status?status=${status}`,
    {
      method: 'PUT',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update complaint status');
  }

  return response.json();
};

// Создание пользователя администратором
const createAdminUser = async ({ phoneNumber, password, username, userInterface = 'WEB', role, email }) => {
  const payload = { phoneNumber, password, username, userInterface, role };
  const em = email != null && String(email).trim() !== '' ? String(email).trim() : '';
  if (em) payload.email = em;

  const response = await authenticatedFetch(`${API_BASE}/admin/users`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = 'Failed to create user';
    try {
      const data = await response.json();
      message = data?.message || message;
    } catch {
      // ignore parse errors, keep default message
    }
    throw new Error(message);
  }

  return response.json();
};

// Получение отзывов/комментариев пользователя
const getUserReviews = async (profileId, page = 0, size = 10) => {
  const requestKey = `${profileId}:${page}:${size}`;
  const now = Date.now();
  const cachedEntry = reviewsResponseCache.get(requestKey);

  if (cachedEntry && now - cachedEntry.timestamp < REVIEWS_CACHE_TTL_MS) {
    return cachedEntry.data;
  }

  if (reviewsRequestInFlight.has(requestKey)) {
    return reviewsRequestInFlight.get(requestKey);
  }

  const requestPromise = (async () => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('size', size);

  const response = await authenticatedFetch(
    `${API_BASE}/support/reviews/${profileId}?${params.toString()}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch reviews: ${response.status} ${response.statusText}`);
  }

    const data = await response.json();
    reviewsResponseCache.set(requestKey, { data, timestamp: Date.now() });
    return data;
  })();

  reviewsRequestInFlight.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    reviewsRequestInFlight.delete(requestKey);
  }
};

// Получение внутренних комментариев по пользователю (заметки саппорта)
const getUserComments = async (userId) => {
  const response = await authenticatedFetch(`${API_BASE}/support/users/${userId}/comments`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user comments: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Добавление внутреннего комментария по пользователю
const addUserComment = async (userId, text) => {
  const params = new URLSearchParams();
  params.append('text', text);

  const response = await authenticatedFetch(
    `${API_BASE}/support/users/${userId}/comment?${params.toString()}`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to add user comment: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Редактирование внутреннего комментария по пользователю
const editUserComment = async (commentId, text) => {
  const params = new URLSearchParams();
  params.append('text', text);

  const response = await authenticatedFetch(
    `${API_BASE}/support/users/comment/${commentId}?${params.toString()}`,
    {
      method: 'PUT',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to edit user comment: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Удаление внутреннего комментария по пользователю
const deleteUserComment = async (commentId) => {
  const response = await authenticatedFetch(`${API_BASE}/support/users/comment/${commentId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete user comment: ${response.status} ${response.statusText}`);
  }

  return true;
};

// Удаление отзыва пользователя (для модератора и выше)
const deleteUserReview = async (reviewId) => {
  const response = await authenticatedFetch(`${API_BASE}/moder/users/reviews/${reviewId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete review: ${response.status} ${response.statusText}`);
  }

  return true;
};

// Получение комментария/отзыва по ID
const getReviewById = async (reviewId) => {
  const response = await authenticatedFetch(`${API_BASE}/support/reviews/comment/${reviewId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch review: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Изменение баланса пользователя
const changeBalance = async (userProfileId, amount, type, reason) => {
  const payload = {
    userProfileId,
    amount,
    type,
    reason
  };

  const response = await authenticatedFetch(`${API_BASE}/moder/users/balance/change`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = 'Failed to change balance';
    try {
      const data = await response.json();
      message = data?.message || message;
    } catch {
      // ignore parse errors, keep default message
    }
    throw new Error(message);
  }

  return response.json();
};

// Создание официального отзыва администратором (для мастеров/провайдеров)
const postAdminReview = async ({ reviewerId, reviewedProfileId, rating, comment }) => {
  const payload = {
    reviewerId,
    reviewedProfileId,
    rating,
    comment: comment?.trim() ? comment.trim() : null
  };

  const response = await authenticatedFetch(`${API_BASE}/admin/reviews`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = 'Failed to create review';
    try {
      const data = await response.json();
      message = data?.message || message;
    } catch {
      // ignore parse errors, keep default message
    }
    throw new Error(message);
  }

  const data = await response.json();
  // Бэкенд возвращает ApiResponse { success, message, data }
  return data?.data ?? data;
};

const updateCompanyVerified = async (userId, verified) => {
  const params = new URLSearchParams();
  params.append('userId', userId);
  params.append('verified', String(Boolean(verified)));

  const response = await authenticatedFetch(`${API_BASE}/moder/users/company/verified?${params.toString()}`, {
    method: 'POST',
  });

  if (!response.ok) {
    let message = 'Failed to update company verification';
    try {
      const data = await response.json();
      message = data?.message || message;
    } catch {
      // ignore parse errors, keep default message
    }
    throw new Error(message);
  }

  try {
    return await response.json();
  } catch {
    return { success: true };
  }
};

// Получение системных логов
const getSystemLogs = async ({
  targetId = '',
  actorId = '',
  type = '',
  actionContains = '',
  from = '',
  to = '',
  page = 0,
  size = 20
} = {}) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('size', size);

  if (targetId && targetId.trim()) params.append('targetId', targetId.trim());
  if (actorId && actorId.trim()) params.append('actorId', actorId.trim());
  if (type && type.trim()) params.append('type', type.trim());
  if (actionContains && actionContains.trim()) params.append('actionContains', actionContains.trim());

  if (from && from.trim()) {
    // datetime-local формат: "YYYY-MM-DDTHH:mm" -> ISO
    try {
      const date = new Date(from);
      if (!isNaN(date.getTime())) {
        params.append('from', date.toISOString());
      }
    } catch (e) {
      console.error('Ошибка парсинга даты from:', e);
    }
  }

  if (to && to.trim()) {
    // datetime-local формат: "YYYY-MM-DDTHH:mm" -> ISO
    try {
      const date = new Date(to);
      if (!isNaN(date.getTime())) {
        params.append('to', date.toISOString());
      }
    } catch (e) {
      console.error('Ошибка парсинга даты to:', e);
    }
  }

  const response = await authenticatedFetch(`${API_BASE}/moder/logs?${params.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch logs: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Получение логов модерации заказов (AI)
const getOrderModerationLogs = async ({
  page = 0,
  size = 20,
  sort = 'createdAt',
  direction = 'DESC',
  userId = ''
} = {}) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('size', size);

  // Spring Pageable ожидает формат sort=field,direction
  if (sort && sort.trim()) {
    params.append('sort', `${sort.trim()},${(direction || 'DESC').toLowerCase()}`);
  }

  const trimmedUserId = userId && userId.trim();
  const endpoint = trimmedUserId
    ? `${API_BASE}/moder/logs/order/user/${encodeURIComponent(trimmedUserId)}`
    : `${API_BASE}/moder/logs/order`;

  const response = await authenticatedFetch(`${endpoint}?${params.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch order logs: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Получение портфолио компании
const getCompanyPortfolio = async (userProfileId) => {
  const params = new URLSearchParams();
  params.append('userProfileId', userProfileId);

  const response = await authenticatedFetch(
    `${API_BASE}/company/portfolio?${params.toString()}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch company portfolio: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Получение купленных заказов провайдера
const getPurchasedOrders = async (userProfileId, page = 0, size = 10, sort = 'createdAt', direction = 'DESC') => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('size', size);
  // Формат сортировки: sort=fieldName,direction (например: sort=updatedAt,desc)
  if (sort) {
    const sortDirection = direction.toLowerCase();
    params.append('sort', `${sort},${sortDirection}`);
  }

  const response = await authenticatedFetch(
    `${API_BASE}/support/orders/purchased/${userProfileId}?${params.toString()}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch purchased orders: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Выход из системы
const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
};

/**
 * Отправка push-уведомления (только для админов).
 * @param {Object} body - PushSendRequest: { title, description, imageUrl?, language, targetType, userProfileIds?, roles?, platform? }
 * @returns {Promise<{ success: boolean, message: string, data: { total, success, failure } }>}
 */
const sendPushNotification = async (body) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/notification/push/send`, {
    method: 'POST',
    body,
  });
  if (!response.ok) {
    const errText = await response.text();
    let message = `Request failed: ${response.status}`;
    try {
      const errJson = JSON.parse(errText);
      if (errJson.message) message = errJson.message;
    } catch (_) { }
    throw new Error(message);
  }
  return response.json();
};

/**
 * Список запланированных (ожидающих) push-уведомлений (только для админов).
 * GET /admin/notification/push/scheduled
 * @returns {Promise<{ success: boolean, message: string, data: Array }>}
 */
const getScheduledPush = async () => {
  const response = await authenticatedFetch(`${API_BASE}/admin/notification/push/scheduled`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errText = await response.text();
    let message = `Request failed: ${response.status}`;
    try {
      const errJson = JSON.parse(errText);
      if (errJson.message) message = errJson.message;
    } catch (_) { }
    throw new Error(message);
  }
  return response.json();
};

/**
 * Отмена запланированного push-уведомления по id (только для админов).
 * DELETE /admin/notification/push/scheduled/{id}
 * @returns {Promise<{ success: boolean, message: string }>}
 */
const cancelScheduledPush = async (id) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/notification/push/scheduled/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errText = await response.text();
    let message = `Request failed: ${response.status}`;
    try {
      const errJson = JSON.parse(errText);
      if (errJson.message) message = errJson.message;
    } catch (_) { }
    throw new Error(message);
  }
  return response.json();
};

/** Значения enum PromoCodeType на бэкенде */
const PROMO_CODE_TYPES = ['PARTNER'];

const parsePromoApiError = async (response, fallback) => {
  let message = fallback;
  try {
    const data = await response.json();
    message = data?.message || message;
  } catch {
    // ignore
  }
  throw new Error(message);
};

const createPromoCode = async (payload) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to create promo code');
  }
  return response.json();
};

const getPromoCodes = async () => {
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes`, {
    method: 'GET',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch promo codes');
  }
  return response.json();
};

const getPromoCodeById = async (id) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${id}`, {
    method: 'GET',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch promo code');
  }
  return response.json();
};

const updatePromoCodeActive = async (id, active) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${id}/active`, {
    method: 'POST',
    body: JSON.stringify({ active }),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update promo code status');
  }
  return response.json();
};

const updatePromoCodeStartsAt = async (id, startsAt) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${id}/starts-at`, {
    method: 'POST',
    body: JSON.stringify({ startsAt }),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update promo code start date');
  }
  return response.json();
};

const updatePromoCodeExpiresAt = async (id, expiresAt) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${id}/expires-at`, {
    method: 'POST',
    body: JSON.stringify({ expiresAt }),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update promo code expiry date');
  }
  return response.json();
};

const getPromoCodeStats = async (id) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/promo-codes/${id}/stats`, {
    method: 'GET',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch promo code stats');
  }
  return response.json();
};

const getPromoCodeActiveUsers = async (id, page = 0, size = 10, sort = []) => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  const sortList = Array.isArray(sort) ? sort : sort ? [sort] : [];
  for (const item of sortList) {
    if (typeof item === 'string' && item) {
      params.append('sort', item);
    } else if (item?.field) {
      const direction = String(item.direction || 'asc').toLowerCase();
      params.append('sort', `${item.field},${direction}`);
    }
  }
  const response = await authenticatedFetch(
    `${API_BASE}/admin/promo-codes/${id}/active-users?${params.toString()}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch promo code active users');
  }
  return response.json();
};

// ── Новостные баннеры (DEV): /api/dev/news-banners ──
const getNewsBanners = async () => {
  const response = await authenticatedFetch(`${API_BASE}/dev/news-banners`, {
    method: 'GET',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch news banners');
  }
  return response.json();
};

const createNewsBanner = async (payload) => {
  const response = await authenticatedFetch(`${API_BASE}/dev/news-banners`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to create news banner');
  }
  return response.json();
};

const updateNewsBanner = async (id, payload) => {
  const response = await authenticatedFetch(`${API_BASE}/dev/news-banners/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update news banner');
  }
  return response.json();
};

const deleteNewsBanner = async (id) => {
  const response = await authenticatedFetch(`${API_BASE}/dev/news-banners/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to delete news banner');
  }
  // Бэкенд отдаёт ApiResponse { success, message }, но тело может отсутствовать
  try {
    return await response.json();
  } catch {
    return { success: true };
  }
};

// ── Маркетплейс ──
// Сервисы маркетплейса (CRUD) — только DEV: /api/dev/marketplace/services.
// Покупки — ADMIN и выше: /api/admin/marketplace/purchases.
const getMarketplaceServices = async () => {
  const response = await authenticatedFetch(`${API_BASE}/dev/marketplace/services`, {
    method: 'GET',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch marketplace services');
  }
  return response.json();
};

const getMarketplaceServiceById = async (id) => {
  const response = await authenticatedFetch(`${API_BASE}/dev/marketplace/services/${id}`, {
    method: 'GET',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch marketplace service');
  }
  return response.json();
};

const createMarketplaceService = async (payload) => {
  const response = await authenticatedFetch(`${API_BASE}/dev/marketplace/services`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to create marketplace service');
  }
  return response.json();
};

const updateMarketplaceService = async (id, payload) => {
  const response = await authenticatedFetch(`${API_BASE}/dev/marketplace/services/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update marketplace service');
  }
  return response.json();
};

const deleteMarketplaceService = async (id) => {
  const response = await authenticatedFetch(`${API_BASE}/dev/marketplace/services/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to delete marketplace service');
  }
  // Бэкенд отдаёт 204 No Content — тела нет.
  return { success: true };
};

// Покупки маркетплейса (пагинированный список + смена статуса).
// Живут под /api/admin — доступны ADMIN и DEV, в отличие от сервисов (DEV-only).
// Эндпоинт принимает стандартный Spring Pageable, поэтому сортировка
// передаётся параметром `sort=<поле>,<asc|desc>`.
const getMarketplacePurchases = async (page = 0, size = 10, sort = 'createdAt', direction = 'DESC') => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('size', String(size));
  if (sort) {
    const dir = String(direction).toLowerCase() === 'asc' ? 'asc' : 'desc';
    params.append('sort', `${sort},${dir}`);
  }
  const response = await authenticatedFetch(
    `${API_BASE}/admin/marketplace/purchases?${params.toString()}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch marketplace purchases');
  }
  return response.json();
};

const updateMarketplacePurchaseStatus = async (id, status) => {
  const response = await authenticatedFetch(`${API_BASE}/admin/marketplace/purchases/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update purchase status');
  }
  return response.json();
};

// ── Транзакции баланса (ADMIN): /api/admin/transactions ──
// Поиск по фильтрам (все опциональны) + стандартный Spring Pageable.
// types — список BalanceTransactionType (можно выбрать несколько),
// createdFrom/createdTo — ISO-8601 с таймзоной (ZonedDateTime на бэкенде).
// Ответ: { transactions: Page<BalanceTransactionDto>, summary: BalanceTransactionSummaryDto }
// — нормализуем в плоский объект страницы с полем summary.
const searchBalanceTransactions = async (
  { userProfileId, orderId, types, createdFrom, createdTo } = {},
  page = 0,
  size = 20,
  sort = 'createdAt',
  direction = 'DESC'
) => {
  const params = new URLSearchParams();
  if (userProfileId) params.append('userProfileId', userProfileId);
  if (orderId) params.append('orderId', orderId);
  (Array.isArray(types) ? types : [types]).filter(Boolean).forEach((type) => {
    params.append('types', type);
  });
  if (createdFrom) params.append('createdFrom', createdFrom);
  if (createdTo) params.append('createdTo', createdTo);
  params.append('page', String(page));
  params.append('size', String(size));
  if (sort) {
    const dir = String(direction).toLowerCase() === 'asc' ? 'asc' : 'desc';
    params.append('sort', `${sort},${dir}`);
  }
  const response = await authenticatedFetch(
    `${API_BASE}/admin/transactions?${params.toString()}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch balance transactions');
  }
  const data = await response.json();
  const pageData = data?.transactions ?? data ?? {};
  return { ...pageData, summary: data?.summary ?? null };
};

// ══════════════════════════════════════════════════════════════
// Чат: /api/chats + загрузка вложений /api/files/chat-attachment
// Контракт бэкенда фиксирован, менять пути/параметры нельзя.
// ══════════════════════════════════════════════════════════════

const getChats = async () => {
  const response = await authenticatedFetch(`${API_BASE}/chats`, { method: 'GET' });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch chats');
  }
  return response.json();
};

// Открыть (или создать) личный чат с собеседником.
const createDirectChat = async (peerId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/direct/${encodeURIComponent(peerId)}`,
    { method: 'POST' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to open direct chat');
  }
  return response.json();
};

// ── Групповые чаты ──
// Создатель считается автоматически, его в memberIds слать не нужно;
// бэкенд требует минимум 3 участника всего и не больше 100.
const createGroupChat = async (title, memberIds) => {
  const response = await authenticatedFetch(`${API_BASE}/chats/groups`, {
    method: 'POST',
    body: JSON.stringify({ title, memberIds }),
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to create group chat');
  }
  return response.json();
};

const addChatMembers = async (chatId, memberIds) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/members`,
    { method: 'POST', body: JSON.stringify({ memberIds }) }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to add members');
  }
  return true;
};

// Выход из группы — это удаление самого себя, отдельного эндпоинта нет.
const removeChatMember = async (chatId, userId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to remove member');
  }
  return true;
};

const updateChatTitle = async (chatId, title) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/title?title=${encodeURIComponent(title)}`,
    { method: 'PUT' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to rename chat');
  }
  return true;
};

// Keyset-пагинация: beforeId — id самого старого уже загруженного сообщения.
const getChatMessages = async (chatId, { beforeId, limit = 30 } = {}) => {
  const params = new URLSearchParams();
  if (beforeId != null) params.append('beforeId', String(beforeId));
  params.append('limit', String(limit));
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/messages?${params.toString()}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch chat messages');
  }
  return response.json();
};

// Фолбэк отправки, когда сокет не подключён. Тело — тот же SendMessageRequest.
const sendChatMessage = async (chatId, payload) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/messages`,
    { method: 'POST', body: JSON.stringify({ ...payload, chatId }) }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to send message');
  }
  return response.json();
};

// Toggle-реакция: сервер возвращает полный агрегат по сообщению.
const reactToChatMessage = async (messageId, emoji) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`,
    { method: 'POST' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to send reaction');
  }
  return response.json();
};

const markChatRead = async (chatId, lastReadMessageId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/read?lastReadMessageId=${lastReadMessageId}`,
    { method: 'POST' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to mark chat as read');
  }
  return true;
};

const setChatPinned = async (chatId, value) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/pinned?value=${value ? 'true' : 'false'}`,
    { method: 'PUT' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update pinned flag');
  }
  return true;
};

const setChatMuted = async (chatId, value) => {
  const response = await authenticatedFetch(
    `${API_BASE}/chats/${encodeURIComponent(chatId)}/muted?value=${value ? 'true' : 'false'}`,
    { method: 'PUT' }
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to update muted flag');
  }
  return true;
};

// Вложения (фото, файлы, голосовые) всегда двумя шагами: сначала файл → id,
// потом сообщение с fileId. XHR вместо fetch — нужен прогресс загрузки.
// Ответ приходит обёрнутым в ApiResponse ({ data: { id, ... } }) — разворачиваем.
const uploadChatFile = (file, { onProgress } = {}) =>
  new Promise((resolve, reject) => {
    const send = (token, isRetry) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/files/chat-attachment`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.responseType = 'json';

      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          const payload = xhr.response;
          // ApiResponse кладёт объект файла в data; если обёртки нет — берём как есть.
          resolve(payload?.data ?? payload);
          return;
        }
        // Один раз пробуем обновить токен и повторить.
        if ((xhr.status === 401 || xhr.status === 403) && !isRetry) {
          try {
            const fresh = await refreshToken();
            send(fresh, true);
            return;
          } catch {
            reject(new Error('Unauthorized'));
            return;
          }
        }
        reject(new Error(xhr.response?.message || `Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));

      const formData = new FormData();
      formData.append('file', file);
      xhr.send(formData);
    };

    send(localStorage.getItem('accessToken'), false);
  });

// ── Публичные файлы (DEV): /api/dev/public-files ──
// Публичная загрузка файлов (например, иконок сервисов маркетплейса).
const getPublicFiles = async () => {
  const response = await authenticatedFetch(`${API_BASE}/dev/public-files`, {
    method: 'GET',
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to fetch public files');
  }
  return response.json();
};

const uploadPublicFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await authenticatedFetch(`${API_BASE}/dev/public-files`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to upload public file');
  }
  return response.json();
};

const deletePublicFile = async (fileKey) => {
  const response = await authenticatedFetch(
    `${API_BASE}/dev/public-files?fileKey=${encodeURIComponent(fileKey)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to delete public file');
  }
  // Бэкенд отдаёт 204 No Content — тела нет.
  return { success: true };
};

// ── Удаление пользователя (DEV): DELETE /api/dev/users/{userProfileId} ──
// Жёсткое удаление профиля без возможности восстановления.
const hardDeleteUser = async (userProfileId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/dev/users/${encodeURIComponent(userProfileId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    await parsePromoApiError(response, 'Failed to delete user');
  }
  userDetailsResponseCache.delete(String(userProfileId));
  try {
    return await response.json();
  } catch {
    return { success: true };
  }
};

// Статистика (модератор / админ): /api/moder/stats — StatsPeriod: DAY | WEEK | MONTH | ALL
const getStatsUsersCount = async (period = 'ALL') => {
  const response = await authenticatedFetch(
    `${API_BASE}/moder/stats/users?period=${encodeURIComponent(period)}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch users stats count: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const getStatsOrdersCount = async (period = 'ALL') => {
  const response = await authenticatedFetch(
    `${API_BASE}/moder/stats/orders?period=${encodeURIComponent(period)}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch orders stats count: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const getStatsReviewsCount = async (period = 'ALL') => {
  const response = await authenticatedFetch(
    `${API_BASE}/moder/stats/reviews?period=${encodeURIComponent(period)}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch reviews stats count: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const getStatsOrdersGraph = async (period) => {
  const response = await authenticatedFetch(
    `${API_BASE}/moder/stats/orders/graph?period=${encodeURIComponent(period)}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch orders graph: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const getStatsUsersGraph = async (period) => {
  const response = await authenticatedFetch(
    `${API_BASE}/moder/stats/users/graph?period=${encodeURIComponent(period)}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch users graph: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const getStatsReviewsGraph = async (period) => {
  const response = await authenticatedFetch(
    `${API_BASE}/moder/stats/reviews/graph?period=${encodeURIComponent(period)}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch reviews graph: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

// Список регионов компаний (для фильтра пользователей по регионам): GET /api/regions?search=
const getRegions = async (search = '', page = 0, size = 20) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('size', size);
  if (search) params.append('search', search);

  const response = await authenticatedFetch(`${API_BASE}/regions?${params.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch regions: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Статистика категорий: GET /api/support/categories/stats
const getCategoryStats = async () => {
  const response = await authenticatedFetch(`${API_BASE}/support/categories/stats`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch category stats: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/moder/stats/users/active — активные пользователи за последние сутки (Long)
const getActiveUsersCount = async () => {
  const response = await authenticatedFetch(`${API_BASE}/moder/stats/users/active`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch active users count: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  const n = typeof data === 'number' ? data : Number(data);
  return Number.isFinite(n) ? n : 0;
};

// Измерение времени ответа API
const measureApiResponseTime = async () => {
  const startTime = performance.now();

  try {
    // Делаем простой запрос для проверки доступа (легкий эндпоинт)
    await authenticatedFetch(`${API_BASE}/support/access`, {
      method: 'GET',
    });

    const endTime = performance.now();
    const responseTime = Math.round(endTime - startTime);
    return responseTime;
  } catch (error) {
    // Если запрос не удался, возвращаем -1 как индикатор ошибки
    console.error('Ошибка измерения времени ответа API:', error);
    return -1;
  }
};

// Экспортируем все функции
export {
  login,
  checkAccess,
  isTokenValid,
  logout,
  refreshToken,
  getFreshAccessToken,
  getUsers,
  getRegions,
  getOrderDetails,
  getOrderCompanies,
  getOrderComments,
  addOrderComment,
  editOrderComment,
  deleteOrderComment,
  searchOrders,
  getUserDetails,
  getComplaints,
  updateUserStatus,
  getComplaintById,
  updateComplaintStatus,
  getUserReviews,
  deleteUserReview,
  getReviewById,
  getUserComments,
  addUserComment,
  editUserComment,
  deleteUserComment,
  createAdminUser,
  updateOrderStatus,
  changeBalance,
  postAdminReview,
  updateCompanyVerified,
  getSystemLogs,
  getOrderModerationLogs,
  getCompanyPortfolio,
  getPurchasedOrders,
  getCategoryStats,
  getStatsUsersCount,
  getStatsOrdersCount,
  getStatsReviewsCount,
  getStatsOrdersGraph,
  getStatsUsersGraph,
  getStatsReviewsGraph,
  getActiveUsersCount,
  measureApiResponseTime,
  sendPushNotification,
  getScheduledPush,
  cancelScheduledPush,
  createPromoCode,
  getPromoCodes,
  getPromoCodeById,
  updatePromoCodeActive,
  updatePromoCodeStartsAt,
  updatePromoCodeExpiresAt,
  getPromoCodeStats,
  getPromoCodeActiveUsers,
  getNewsBanners,
  createNewsBanner,
  updateNewsBanner,
  deleteNewsBanner,
  getMarketplaceServices,
  getMarketplaceServiceById,
  createMarketplaceService,
  updateMarketplaceService,
  deleteMarketplaceService,
  getMarketplacePurchases,
  updateMarketplacePurchaseStatus,
  searchBalanceTransactions,
  getChats,
  createDirectChat,
  createGroupChat,
  addChatMembers,
  removeChatMember,
  updateChatTitle,
  getChatMessages,
  sendChatMessage,
  reactToChatMessage,
  markChatRead,
  setChatPinned,
  setChatMuted,
  uploadChatFile,
  getPublicFiles,
  uploadPublicFile,
  deletePublicFile,
  hardDeleteUser,
  PROMO_CODE_TYPES,
  ALLOWED_ROLES,
  API_BASE,
  BASE_URL
};